/**
 * Edit-profile screen and sidebar for admin-level profile field overrides.
 *
 * `EditProfile` renders the profile view alongside `EditProfileSidebar`, which
 * exposes admin-only controls (name override, photo override, title selector).
 * Each override widget uses a `Toggle` + `SaveableTextField` pair so changes
 * are persisted to the server only when the admin explicitly saves them.
 */

import { MANAGE_PROFILE_DETAILS_CAPABILITY } from '@repo/shared/roles-capabilities';
import { createThemedStyles } from '@repo/theme/styles';
import { RestrictedScreen } from 'component/admin';
import { Pad, PadBox, Separator, WindowTitle } from 'component/basics';
import { ConversationScreen } from 'component/screen';
import { Heading } from 'component/text';
import { useProfile } from 'data/profile';
import { useSettings } from 'data/settings';
import React from 'react';
import { ScrollView, View } from 'react-native';
import type { ProfileConfig } from 'structure/profile';
import { Profile } from 'structure/profile';
import { useInstanceKey } from 'util/datastore';
import { useConfig } from 'util/features-hooks';
import { t } from 'util/stdtypes';

export function EditProfile() {
    const s = useEditProfileScreenStyle();
    const userId = useInstanceKey();
    const { profile } = useProfile(userId);
    const name = profile?.name ?? '';

    return (
        <RestrictedScreen capability={MANAGE_PROFILE_DETAILS_CAPABILITY}>
            <WindowTitle formatParams={{ name }} label={t('profile.edit.windows_title', 'Edit: {name}')} />
            <View style={s.screen}>
                <ConversationScreen>
                    <Pad />
                    <Profile />
                </ConversationScreen>
                <EditProfileSidebar />
            </View>
        </RestrictedScreen>
    );
}
function EditProfileSidebarHeader() {
    return (
        <PadBox horiz={20} vert={20}>
            <Heading label={t('profile.edit.heading', 'Edit')} type='large' weight='medium' />
        </PadBox>
    );
}

function EditProfileSidebarSection({ headingLabel, headingText, children }: any) {
    return (
        <PadBox horiz={20} vert={20}>
            <Heading label={headingLabel} text={headingText} type='small' weight='medium' />
            <Pad size={32} />
            {children}
        </PadBox>
    );
}

export function EditProfileSidebar() {
    const s = useEditProfileSidebarStyle();

    const { profileEditSidebarWidgets } = useConfig<ProfileConfig>();
    const settings = useSettings();
    return (
        <View style={s.sidebar}>
            <ScrollView>
                <EditProfileSidebarHeader />
                <Separator />
                {profileEditSidebarWidgets
                    ?.filter(({ isVisible }) => (isVisible ? isVisible(settings) : true))
                    .map(({ label, Widget }) => {
                        return (
                            <React.Fragment key={label.key ?? label.defaultText}>
                                <EditProfileSidebarSection headingLabel={label}>
                                    <Widget />
                                </EditProfileSidebarSection>
                                <Separator />
                            </React.Fragment>
                        );
                    })}
            </ScrollView>
        </View>
    );
}
const useEditProfileSidebarStyle = createThemedStyles((tokens) => ({
    sidebar: {
        width: 400,
        borderLeftWidth: 1,
        borderLeftColor: tokens.colors.colorBorderPrimary,
    },
}));
const useEditProfileScreenStyle = createThemedStyles((tokens) => ({
    screen: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: tokens.colors.colorSurfacePrimary,
    },
}));
